import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import {
  ERROR_CODES,
  toast,
  getTranslatedText,
  useIntl,
  EventEmitter,
  COMMON_CONSTANTS
} from '@nile/components';
import { getState } from '@nile/components/src/Managers/StateManager';
import { getHeaderValue } from '@nile/components/src/Utils';
import envConfig from '@nile/home/src/Config/env';
import hashHistory from '@nile/home/src/hashHistory';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import gql from 'graphql-tag';
import React from 'react';
import { loggerInfo } from '@nile/home/src/coralogixRUM';

const cache = new InMemoryCache({
  addTypename: false
});

const mockCache = new InMemoryCache({
  addTypename: false
});

const ticketCache = new InMemoryCache({
  addTypename: false
});

const accountsCache = new InMemoryCache({
  addTypename: false
});

const rmmCache = new InMemoryCache({
  addTypename: false
});

const mlCache = new InMemoryCache({
  addTypename: false
});

const getCookieValue = (name) =>
  document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';

function genHexString(len) {
  const maxlen = 8;
  const min = Math.pow(16, Math.min(len, maxlen) - 1);
  const max = Math.pow(16, Math.min(len, maxlen)) - 1;
  const n = Math.floor(Math.random() * (max - min + 1)) + min;
  let r = n.toString(16);
  while (r.length < len) {
    r = r + genHexString(len - maxlen);
  }
  return r;
}

const requestLink = setContext((_, { headers }) => {
  const currentUserRole = localStorage.getItem('currentServicePortal');
  const traceId = genHexString(32);
  const traceSpanId = '0000000000000000';
  const spanId = genHexString(16);
  const samplingState = '1';
  const version = '00';
  const traceFlags = '01';
  const headerData = {
    headers: {
      ...headers,
      ...getHeaderValue(),
      'X-B3-TraceId': traceId,
      'X-B3-SpanId': spanId,
      b3: `${traceId}-${spanId}-${samplingState}`,
      traceparent: `${version}-${traceId}-${traceSpanId}-${traceFlags}`
    }
  };
  if (currentUserRole) {
    headerData.headers['x-user-role-type'] = currentUserRole;
  }
  return headerData;
});

const getMockHeaderValue = (queryObject) => {
  return {
    'x-mock-response-name':
      queryObject?.query?.definitions?.[0]?.selectionSet?.selections?.[0]?.name
        ?.value
  };
};

const mockRquestLink = setContext((queryObject, arg) => {
  return {
    headers: {
      ...arg.headers,
      ...getMockHeaderValue(queryObject, arg),
      'X-B3-TraceId': genHexString(16),
      'X-B3-SpanId': genHexString(16)
    }
  };
});

const errorLink = onError((params) => {
  console.log(params, 'params');
  const { graphQLErrors, networkError, operation, forward } = params;
  const context = operation.getContext();
  const { headers, serviceName } = context;
  var timerId;

  const isDevEnv = ['development', 'alpha'].includes(process.env.NODE_ENV);

  const info = {
    hrefString: location.href,
    userAgent: navigator.userAgent,
    id: getCookieValue('uitoken'),
    headers: headers,
    query: operation?.query?.loc?.source?.body,
    variables: operation?.variables
  };

  const clientError = graphQLErrors?.[0]?.extensions?.clientError?.[0];

  if (graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
    switch (graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode) {
      case 563:
        hashHistory.push('/onboard/billing', { errors: graphQLErrors });
        break;
      case 564:
        hashHistory.push('/error/subscriptionPaused', {
          errors: graphQLErrors
        });
        break;
      case 1005:
      case 1010:
        const attributes = clientError?.param?.attributes;
        const fields = { name: window.TranslationMessages['common.name'] };
        if (attributes) {
          if (attributes.length === 1) {
            fields.name = window.TranslationMessages[attributes[0]];
          } else {
            fields.name = attributes
              .map((attribute) => window.TranslationMessages[attribute])
              .join(', ');
          }
        }
        toast('error', `app.errorCode.${clientError.errorCode}`, 'en', fields);
        break;
      case 565:
        hashHistory.push('/error/subscriptionCancelled', {
          errors: graphQLErrors
        });
        break;
      case 566:
        toast('error', 'app.errorCode.566');
        break;
      case 554:
        //TODO add intl
        if (graphQLErrors[0]?.extensions?.clientError[0]?.param) {
          for (let i in graphQLErrors[0]?.extensions?.clientError[0]?.param) {
            toast('error', i);
          }
        }
        break;
      case 1027:
        hashHistory.push('/error/ssoError?errorCode=1027', {
          errors: graphQLErrors
        });
        break;
      case 1028:
        hashHistory.push('/error/ssoError?errorCode=1028', {
          errors: graphQLErrors
        });
        break;
      case 20111:
      case 90055:
        toast(
          'error',
          graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorMessage
        );
        break;
      case 20112: // bit defender - display the error message that we get from bitDefender itself (lokalise not required)
      case 20113: // acronis - display the error message that we get from acronis (lokalise not required)
      case 20117: // new integration framework, dynamic error message
        toast(
          'error',
          graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorMessage
        );
        break;
      case 2011:
        if (graphQLErrors[0]?.extensions?.clientError[0]?.param) {
          const intl = useIntl();
          const errorIdentifiers = graphQLErrors[0]?.extensions?.clientError[0]?.param?.items?.map(
            (el) => {
              return getTranslatedText(
                intl,
                `admin.identificationNumbers.${el}`
              );
            }
          );
          toast(
            'error',
            `app.errorCode.${clientError.errorCode}.invalidIdentificationNumbers`,
            'en',
            {
              br: <b>{`( ${errorIdentifiers.join(',')} )`}</b>
            }
          );
        }
        break;
      case 10012:
        const errorData = graphQLErrors?.[0]?.extensions?.clientError;
        if (errorData.length > 1) {
          EventEmitter.dispatch('bulkClosureError', errorData);
        } else {
          EventEmitter.dispatch('closureError', errorData?.[0]);
        }
        break;
      case 50001:
      case 50002:
      case 50003:
      case 50004:
        const errorCode =
          graphQLErrors?.[0]?.extensions?.clientError?.[0]?.errorCode;
        EventEmitter.dispatch('aiError', {
          errorCode: errorCode
        });
        break;
      case 10015:
        const errorDta = graphQLErrors?.[0]?.extensions?.clientError;
        toast('error', `app.errorCode.10015`);
        if (errorDta) {
          EventEmitter.dispatch('closeGlobalConversation', 'agentChatWindow');
        }
        break;
      case 1040:
        const intl = useIntl();
        const errCode = graphQLErrors[0].extensions.clientError[0].errorCode;
        const errorMessage = ERROR_CODES[errCode] || `app.errorCode.${errCode}`;
        const duplicateClients = graphQLErrors[0].extensions.clientError[0].param?.clients?.[0]
          ?.map((client) => client.name)
          ?.join(', ');
        const message = `${getTranslatedText(
          intl,
          errorMessage
        )} ${duplicateClients}`;
        toast('error', message);
        break;
      default:
        /**
         * arg[1] - String (Error type)
         * arg[2] - String (Error message)
         * arg[3] - String (Language)
         * arg[4] - Object (Message dynamic message fields)
         * arg[5] - String (Toaster id)
         */
        {
          const errCode = graphQLErrors[0].extensions.clientError[0].errorCode;
          const errorMessage =
            ERROR_CODES[errCode] || `app.errorCode.${errCode}`;
          toast('error', errorMessage);
        }

        break;
    }
  } else if (networkError) {
    switch (networkError?.statusCode) {
      case 278:
        window.location.replace(networkError?.result?.redirectURL);
        break;
      case 561: // SESSION_EXPIRED
      case 570: // No CSRF Token provided or invalid
        toast('info', 'app.common.session.timeout.toast.message');
        if (timerId) {
          return;
        }
        timerId = setTimeout(() => {
          if (networkError?.statusCode === 570) {
            document.cookie = 'app-csrf' + '=; Path=/; Max-Age=-99999999;';
            document.cookie = 'uitoken' + '=; Path=/; Max-Age=-99999999;';
          }
          window.location.reload();
        }, 6000);
        // hashHistory.push('/error/sessionExpired', { errors: networkError });
        break;
      case 502:
        // hashHistory.push('/error/connectivityIssue', { errors: networkError });
        break;
      case 503:
        EventEmitter.dispatch('serviceUnavailableError', {
          serviceName: serviceName
        });
        break;
      case 504:
        // hashHistory.push('/error/serviceTimeout', { errors: networkError });
        break;
      case 520:
        if (networkError?.result) {
          if (networkError?.result?.error?.includes('Under Maintenance')) {
            if (window.location.hash !== '#/error/underMaintenance')
              window.localStorage.setItem(
                'previousRoute',
                JSON.stringify({
                  route: window.location.hash,
                  maintenance: true
                })
              );
            if (timerId) {
              clearTimeout(timerId);
            }
            timerId = setTimeout(
              () =>
                hashHistory.push('/error/underMaintenance', {
                  errors: networkError
                }),
              3000
            );
          }
        } else {
          if (window.location.hash !== '#/error/underMaintenance')
            window.localStorage.setItem(
              'previousRoute',
              JSON.stringify({
                route: window.location.hash,
                maintenance: true
              })
            );
          if (timerId) {
            clearTimeout(timerId);
          }
          timerId = setTimeout(
            () =>
              hashHistory.push('/error/underMaintenance', {
                errors: networkError
              }),
            3000
          );
        }
        break;
      case 562: // ACCOUNT EXPIRED
        hashHistory.push('/error/accountExpired', { errors: networkError });
        break;
      case 553: // CONFIGURE_TFA
        hashHistory.push('/error/configureTfa', { errors: networkError });
        break;
      case 552: // RESTRICTED_ACCESS
      case 403:
        hashHistory.push('/error/restrictedAccess', { errors: networkError });
        break;
      case 402:
      case 563:
        hashHistory.push('/onboard/billing', { errors: networkError });
        break;
      case 564:
        hashHistory.push('/error/subscriptionPaused', {
          errors: networkError
        });
        break;
      case 565:
        hashHistory.push('/error/subscriptionCancelled', {
          errors: networkError
        });
        break;
      case 413:
        toast('error', 'app.asset.attachment.file.large.error.message');
        break;
      case 401:
        hashHistory.push('/error/unauthorizedAccess', { errors: networkError });
        break;
      case 551: // PASSWORD_EXPIRED
        hashHistory.push('/error/passwordExpired', { errors: networkError });
        break;
      case 568: // ITEM DELETED
        hashHistory.push('/error/itemNotFound', { errors: networkError });
        break;
      case 1027:
        hashHistory.push('/error/ssoError?errorCode=1027', {
          errors: graphQLErrors
        });
        break;
      case 1028:
        hashHistory.push('/error/ssoError?errorCode=1028', {
          errors: graphQLErrors
        });
        break;
      default:
      /**
       * arg[1] - String (Error type)
       * arg[2] - String (Error message)
       * arg[3] - String (Language)
       * arg[4] - Object (Message dynamic message fields)
       * arg[5] - String (Toaster id)
       */
      // toast('error', 'app.toast.error.message', 'en', undefined, 'network-default-error');
    }
  } else {
    /**
     * arg[1] - String (Error type)
     * arg[2] - String (Error message)
     * arg[3] - String (Language)
     * arg[4] - String (Message dynamic message fields)
     * arg[5] - String (Toaster id)
     */
    // toast('error', 'app.toast.error.message', 'en', undefined, 'network-default-error');
  }

  if (isDevEnv && networkError?.statusCode) {
    toast('error', `Network Error: ${networkError.statusCode}`);
  }

  if (isDevEnv && graphQLErrors) {
    let apiName =
      params?.operation?.operationName ??
      Object.keys(params?.response?.data ?? {})[0];
    apiName = apiName ? `${apiName} : ` : '';
    // toast('error', `${apiName}${graphQLErrors[0]?.message}`);
  }

  function extractQueryName(inputString) {
    if (!inputString) {
      return null;
    }

    const trimmed = inputString.replace(/\s+/g, ' ').trim();

    const match = trimmed.match(/{\s*(\w+)/);
    return match ? match[1] : null;
  }

  function getRequestUriFromServiceName() {
    const serviceMap = {
      RMMService: envConfig.rmmGateway,
      TicketService: envConfig.ticketGateway,
      AccountService: envConfig.accountsGateway,
      SignupService: envConfig.camGateway,
      TempService: envConfig.accountsGateway,
      NonAuthService: envConfig.accountsGateway,
      MLService: envConfig.mlGateway,
      commonService: null
    };

    return serviceMap[serviceName] || null;
  }

  if (networkError) {
    const queryName = extractQueryName(info?.query);
    info.queryName = queryName;
    info.apiDomain = localStorage?.getItem('apiDomain');
    info.dcRegion = localStorage?.getItem('dcRegion');
    info.requestUri = getRequestUriFromServiceName();
  }

  // if (window?.logger) {
  //   logger.info('Network graphql request', {
  //     graphQLErrors,
  //     networkError,
  //     info
  //   });
  //   logger.flush();
  // }

  loggerInfo('Network graphql request', {
    graphQLErrors,
    networkError,
    info
  });
});

function handlePerformanceEntries(entries) {
  if (window.performance) {
    const performanceTiming = window.performance.timing;
    const dnsLookupTime =
      performanceTiming.domainLookupEnd - performanceTiming.domainLookupStart;
    const tcpConnectTime =
      performanceTiming.connectEnd - performanceTiming.connectStart;
    const requestTime =
      performanceTiming.responseEnd - performanceTiming.requestStart;
    const totalTime =
      performanceTiming.loadEventEnd - performanceTiming.navigationStart;

    // console.error(`TCP Connect Time: ${tcpConnectTime} ms`);
    // console.error(`Request Time: ${requestTime} ms`);
    // console.error(`Total Time: ${totalTime} ms`);
    entries.getEntries().forEach((entry) => {
      console.error(`Performance Entry: ${entry.name}`, entry);
      if (logger && entry.name.indexOf('/api') > -1) {
        // logger.info('Performance Network graphql request', {
        //   entry
        // });

        loggerInfo('Performance Network graphql request', { entry });
      }
    });
    logger.flush();
  }
}

window.addEventListener(
  'hashchange',
  () => {
    try {
      if (location && location.hash.indexOf('debug=true') > -1) {
        if (PerformanceObserver) {
          const performanceObserver = new PerformanceObserver(
            handlePerformanceEntries
          );
          performanceObserver.observe({
            entryTypes: ['resource', 'navigation']
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  },
  false
);

const headerLink = new ApolloLink((operation, forward) => {
  const fe_start_time = new Date();
  operation.setContext({ start: fe_start_time });

  return forward(operation).map((response) => {
    /**
     * try to normalise response data from here
     */
    const startTime = new Date();
    const source_url = window.location;
    const time = startTime - operation.getContext().start;
    try {
      // log requests taking more then 3 seconds time
      if (time / 1000 > 3) {
        const performanceLogParameters = {
          elapsed_time: time,
          fe_start_time: fe_start_time?.toISOString(),
          end_point:
            operation?.query?.definitions?.[0]?.selectionSet?.selections?.[0]
              ?.name?.value,
          userAgent: navigator.userAgent,
          headers: operation.getContext()?.headers,
          customerId: getCookieValue('uitoken'),
          appVersion: COMMON_CONSTANTS.APP_VERSION,
          hashURL: source_url?.hash,
          parent_module: source_url.hash.split?.('/')?.[1]
        };
        // console.log('Performance Network graphql request', {
        //   performanceLogParameters
        // });

        // if (logger) {
        // logger.info('Graphql API monitoring', {
        //   performanceLogParameters
        // });
        // logger.flush();

        // }
        loggerInfo('Graphql API monitoring', {
          performanceLogParameters
        });
      }
    } catch (e) {}
    if (location.hash.indexOf('debug=true') > -1) {
      console.error(
        `Operation ${operation?.query?.loc?.source?.body} took ${time /
          1000} seconds to complete`
      );
      let ninfo = {
        body: operation?.query?.loc?.source?.body,
        time: time / 1000
      };
      // if (logger) {
      //   logger.info('Performance Network graphql request', {
      //     ninfo
      //   });
      //   logger.flush();

      // }
      loggerInfo('Performance Network graphql request', { ninfo });
    }
    return response;
  });
});

// const headerLink = new ApolloLink((operation, forward) => {
//   return forward(operation).map((response) => {
//     /**
//      * try to normalise response data from here
//      */
//     return response;
//   });
// });

const MockTicketClient = new ApolloClient({
  // cache: mockCache,
  cache,
  link: mockRquestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.mockTicketGateway
        })
      )
    )
  ),
  connectToDevTools: true
});

const MockRMMClient = new ApolloClient({
  // cache: mockCache,
  cache,
  link: mockRquestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.mockRMMGateway
        })
      )
    )
  ),
  connectToDevTools: true
});

const TempAccountClient = new ApolloClient({
  cache,
  link: requestLink.concat(
    headerLink.concat(
      createHttpLink({
        uri: envConfig.accountsGateway,
        credentials: 'include'
      })
    )
  ),
  connectToDevTools: true
});

const AccountClient = new ApolloClient({
  cache,
  link: requestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.accountsGateway,
          credentials: 'include'
        })
      )
    )
  ),
  // defaultOptions: {
  //   mutate: {
  //     errorPolicy: 'all',
  //     fetchPolicy: 'no-cache'
  //   }
  // },
  connectToDevTools: true
});

const AccountClientWithDeduplication = new ApolloClient({
  cache,
  link: requestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: localStorage?.getItem('apiDomain') + envConfig.accountsGateway,
          credentials: 'include'
        })
      )
    )
  ),
  queryDeduplication: false,
  connectToDevTools: true
});

const SystrayClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false
  }),
  link: createHttpLink({
    uri: localStorage?.getItem('apiDomain') + envConfig.ticketGateway,
    credentials: 'include'
  })
});

const SystrayAccountClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false
  }),
  link: createHttpLink({
    uri: localStorage?.getItem('apiDomain') + envConfig.accountsGateway,
    credentials: 'include'
  })
});

const getServiceLink = (serviceName) => {
  return setContext(() => ({
    serviceName: serviceName || 'commonService'
  }));
};

const updateClientUri = (resUri) => {
  const services = [
    {
      client: TicketClient,
      uri: envConfig.ticketGateway,
      serviceName: 'TicketService'
    },
    {
      client: SignupClient,
      uri: envConfig.camGateway,
      serviceName: 'SignupService'
    },
    {
      client: AccountClient,
      uri: envConfig.accountsGateway,
      serviceName: 'AccountService'
    },
    {
      client: TempAccountClient,
      uri: envConfig.accountsGateway,
      serviceName: 'TempService'
    },
    {
      client: RMMClient,
      uri: envConfig.rmmGateway,
      serviceName: 'RMMService'
    },
    {
      client: NonAuthAccountClient,
      uri: envConfig.accountsGateway,
      serviceName: 'NonAuthService'
    },
    {
      client: MLClient,
      uri: envConfig.mlGateway,
      serviceName: 'MLService'
    }
  ];
  const clientsToUpdate = [
    TicketClient,
    SignupClient,
    AccountClient,
    TempAccountClient,
    RMMClient,
    NonAuthAccountClient,
    MLClient
  ];
  const newUris = [
    envConfig.ticketGateway,
    envConfig.camGateway,
    envConfig.accountsGateway,
    envConfig.accountsGateway,
    envConfig.rmmGateway,
    envConfig.accountsGateway,
    envConfig.mlGateway
  ];

  const updatePromises = clientsToUpdate.map((client, index) => {
    return new Promise((resolve, reject) => {
      const uri = localStorage.getItem('apiDomain')
        ? localStorage.getItem('apiDomain') + newUris[index]
        : newUris[index];

      client.setLink(
        getServiceLink(services[index].serviceName).concat(
          requestLink.concat(
            headerLink.concat(
              errorLink.concat(
                createHttpLink({
                  uri: uri,
                  credentials: 'include',
                  headers:
                    client === NonAuthAccountClient
                      ? {
                          ...getHeaderValue(),
                          authorization: `Bearer nl-${token}`
                        }
                      : {}
                })
              )
            )
          )
        )
      );
      resolve();
    });
  });

  return Promise.all(updatePromises);
};

const updateSysClientUri = () => {
  const clientsToUpdate = [SystrayClient, SystrayAccountClient];
  const newUris = [envConfig.ticketGateway, envConfig.accountsGateway];
  const agentProp = JSON.parse(sessionStorage.getItem('agentProperties'));

  const updatePromises = clientsToUpdate.map((client, index) => {
    return new Promise((resolve, reject) => {
      client.setLink(
        createHttpLink({
          uri: localStorage.getItem('apiDomain')
            ? localStorage.getItem('apiDomain') + newUris[index]
            : newUris[index],
          credentials: 'include',
          headers: {
            'asset-id': agentProp?.AssetId,
            'asset-token': agentProp?.AssetToken,
            'app-acc': agentProp?.AppAcc,
            reqType: 'assetUser',
            'X-SO-UA-Build-Version': '1.7'
          }
        })
      );
      resolve();
    });
  });

  return Promise.all(updatePromises);
};

const getNonAuthToken = () => {
  const hash = window.location.hash;
  const queryString = hash.split(/[?&]/);
  let token = null;
  for (const part of queryString) {
    if (part.startsWith('token=')) {
      token = part.split('=')[1]; // return only value
    }
  }
  return token;
};

const token = getNonAuthToken();

const NonAuthAccountClient = new ApolloClient({
  // cache: accountsCache,
  cache,
  link: errorLink.concat(
    createHttpLink({
      uri: envConfig.accountsGateway,
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        authorization: `Bearer nl-${token}`
      }
    })
  ),
  connectToDevTools: true
});

const TicketClient = new ApolloClient({
  // cache: ticketCache,
  cache,
  link: requestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.ticketGateway,
          credentials: 'include'
        })
      )
    )
  ),
  // defaultOptions: {
  //   mutate: {
  //     errorPolicy: 'all',
  //     fetchPolicy: 'no-cache'
  //   }
  // },
  connectToDevTools: true
});

const SignupClient = new ApolloClient({
  // cache: accountsCache,
  cache,
  link: createHttpLink({
    uri: envConfig.camGateway,
    credentials: 'include'
  }),
  connectToDevTools: true
});

const RMMClient = new ApolloClient({
  // cache: rmmCache,
  cache,
  link: requestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.rmmGateway,
          credentials: 'include'
        })
      )
    )
  ),
  // defaultOptions: {
  //   mutate: {
  //     errorPolicy: 'all',
  //     fetchPolicy: 'no-cache'
  //   }
  // },
  connectToDevTools: true
});

let cookie = document.cookie;

const MLClient = new ApolloClient({
  // cache: mlCache,
  cache,
  link: requestLink.concat(
    headerLink.concat(
      errorLink.concat(
        createHttpLink({
          uri: envConfig.mlGateway,
          credentials: 'include'
        })
      )
    )
  ),
  // defaultOptions: {
  //   mutate: {
  //     errorPolicy: 'all',
  //     fetchPolicy: 'no-cache'
  //   }
  // },
  connectToDevTools: true
});

// cache.writeData({
//   data: {
//     isLoggedIn: !!(
//       cookie.match(/uitoken\=([^;]*)/) && // eslint-disable-next-line
//       cookie.match(/uitoken\=([^;]*)/).length > 0 && // eslint-disable-next-line
//       cookie.match(/uitoken\=([^;]*)/)[1].length > 0 // eslint-disable-next-line
//     ),
//   },
// });

cache.writeQuery({
  query: gql`
    query isLoggedIn {
      isLoggedIn
    }
  `,
  data: {
    isLoggedIn: !!(
      (
        cookie.match(/uitoken\=([^;]*)/) && // eslint-disable-next-line
        cookie.match(/uitoken\=([^;]*)/).length > 0 && // eslint-disable-next-line
        cookie.match(/uitoken\=([^;]*)/)[1].length > 0 &&
        cookie.match(/app-csrf\=([^;]*)/) &&
        cookie.match(/app-csrf\=([^;]*)/).length > 0 &&
        cookie.match(/app-csrf\=([^;]*)/)[1].length > 0
      ) // eslint-disable-next-line
    )
  }
});

export {
  TicketClient,
  SignupClient,
  AccountClient,
  AccountClientWithDeduplication,
  TempAccountClient,
  RMMClient,
  cache,
  MockTicketClient,
  NonAuthAccountClient,
  getNonAuthToken,
  MockRMMClient,
  MLClient,
  SystrayClient,
  SystrayAccountClient,
  updateClientUri,
  updateSysClientUri
};
